import React, { useState } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';

import imageModifier from '../../../utils/imageModifier';
import { imageSrcKind, needsCdnRewrite, imagePatchFromSelection } from '../../../utils/emailImageSrc';

/*
The image block's `src`, edited the way the rest of this CMS edits an image.

WHAT IT IS. A thumbnail, a button that opens the media library, and the URL
itself as text underneath. The button is the normal path — that is what was
asked for, and it is the only one that does not require knowing a URL. The text
field is the escape hatch, and it is not optional.

WHY THE TEXT FIELD STAYS. The 26 converted PRO networking templates have a hero
whose src is literally `{{{imageUrl}}}`: the sending code picks the image per
recipient. Replace the field with a picker and that value becomes unreadable and
unsettable, and the first editor who opens one of those templates and touches
the image bakes in a fixed URL that breaks every send. So the URL is always
visible, always typeable, and labelled so that a variable is obviously welcome
there — the same promise the button block's "Links to" field already makes.

WHY THE THUMBNAIL BRANCHES. `<img src="{{{imageUrl}}}">` resolves to nothing and
draws a broken-image icon. That reads as a fault, and the obvious way to "fix" a
fault is to pick a picture — which is exactly the destructive act. So a src
holding Handlebars is drawn as the expression itself with a line saying who
fills it in. The decision is `imageSrcKind` in src/utils/emailImageSrc.js, where
it is tested; nothing here parses a src.

WHAT IT REUSES. UIChooseImageModal → SingleObjUploaderWithSelector is the exact
chain the page-builder blocks use (see DragAndDrop/ItemsComponents/ui/UIImage).
Upload-a-new-asset and pick-an-existing-one both already live in it. Those
components carry their own Tailwind classes and are not restyled here — this
file hosts the modal, it does not dress it.

WHY @loadable. The media library pulls in react-dropzone, axios, the MAM cards
and their queries. None of that belongs in the chunk that draws the inspector,
and none of it is needed until someone clicks the button — so the modal is code
split exactly the way RichTextEditor is, ssr:false because Uploader's dropzone
and the modal's Portal both want a DOM and /cms/:tab is server-rendered.
*/
const ChooseImageModal = loadable(() => import('../DragAndDrop/ItemsComponents/ui/UIChooseImageModal'), {
  ssr: false,
  fallback: null,
});

// Big enough to be a real preview in a ~190px inspector track on a retina
// screen, small enough that opening the inspector does not pull a 4MB original
// down the wire. imageModifier hands anything it does not recognise back whole,
// so a non-thumbor URL simply loads as itself.
const PREVIEW_SIZE = '600x600';

// What is baked into the email's src when an asset is picked.
//
// WHY NOT THE URL THE LIBRARY HANDS BACK. That is an ORIGIN url on
// assets.primaverasound.com, and it does not reliably load in a browser — an
// asset picked from the library drew a broken image in the canvas while the
// inspector's own thumbnail, two inches away, rendered perfectly. The thumbnail
// goes through imageModifier and the canvas did not: the difference is the whole
// bug. assets-img.primaverasound.com is what actually serves.
//
// Every other screen gets away with storing the origin url because it renders
// through imageModifier on the way to the browser. An email cannot — what is
// stored is what lands in an inbox, where nothing will transform it.
//
// 1200 is twice the 600px these emails are built for, so it stays sharp on a
// retina screen. Measured against a real asset: fit-in/1200x1200 was the
// smallest of the transforms tried (1.43MB, against 2.47MB at 1100). fit-in
// preserves the aspect ratio inside the box, which matters because the asset's
// shape is not known here. Fixed rather than derived from the block's width, so
// changing the width later is only an attribute change and never leaves the src
// pointing at an image too small for it.
const EMAIL_ASSET_SIZE = '1200x1200';

const ImageSourceField = ({
  id, field, value, alt, onPatch,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const src = typeof value === 'string' ? value : '';
  const kind = imageSrcKind(src);

  // The media library's selectCallback. It hands back an array; the util turns
  // it into the props to write and decides whether `alt` is one of them, so
  // that "never clobber alt the editor wrote" is a tested rule rather than a
  // line of JSX.
  // Not public means the library itself does not use the plain url for this asset
  // — it fetches a signed one (see Mam/MamObject.jsx). Whether such an asset is
  // reliably deliverable to a recipient's inbox has NOT been established here, so
  // this says so plainly rather than pretending either way. A test send is the
  // cheap way to settle it, and the button for one is on this same screen.
  const [pickedPrivate, setPickedPrivate] = useState(false);

  const chooseFromLibrary = (selected) => {
    setPickerOpen(false);
    const patch = imagePatchFromSelection(selected, alt);
    if (!patch) return;

    const asset = Array.isArray(selected) ? selected[0] : selected;
    setPickedPrivate(Boolean(asset) && asset.isPublic === false);

    // See EMAIL_ASSET_SIZE: store the url that actually serves, not the origin
    // one the library returns.
    //
    // Guarded, because imageModifier is not idempotent: run it on a url already
    // pointing at the CDN and it rewrites the host again, giving
    // assets-img.img.primaverasound.com/fit-in/1200x1200/600x600/… — a host that
    // does not exist and a doubled size segment. Reachable simply by picking an
    // image twice.
    onPatch({ ...patch, src: needsCdnRewrite(patch.src) ? imageModifier(patch.src, EMAIL_ASSET_SIZE, true) : patch.src });
  };

  return (
    <div className="field inspector-field wide">
      <span className="field-label">Image</span>

      <div className="imagepick">
        <div className={`imagepick-thumb is-${kind}`}>
          {kind === 'url' && (
            // Decorative here: the block's real alt text is edited in its own
            // field two rows down, and repeating it would have a screen reader
            // read the same string twice.
            <img src={imageModifier(src, PREVIEW_SIZE)} alt="" />
          )}
          {kind === 'variable' && <span className="imagepick-variable">{src.trim()}</span>}
          {kind === 'empty' && <span className="imagepick-empty">No image yet</span>}
        </div>

        <div className="imagepick-side">
          <button type="button" className="btn tiny" onClick={() => setPickerOpen(true)}>
            {kind === 'empty' ? 'Choose image' : 'Change image'}
          </button>
          {kind === 'variable' && (
            <p className="hint">
              The campaign supplies this image when the email is sent. Choosing one here would
              replace the variable with a fixed picture for every recipient.
            </p>
          )}
          {kind !== 'variable' && !pickedPrivate && (
            <p className="hint">
              Upload a new image or pick one already in the media library.
            </p>
          )}
          {kind !== 'variable' && pickedPrivate && (
            <p className="hint emails-asset-private">
              {'This asset is not marked public in the media library. Send yourself a test '}
              {'before using it — an image that will not load for recipients shows as a '}
              {'broken box in the inbox.'}
            </p>
          )}
        </div>
      </div>

      <label htmlFor={id}>
        {field.label}
        <input
          id={id}
          type="text"
          className="textinput mono"
          value={src}
          onChange={event => onPatch({ [field.key]: event.target.value })}
        />
      </label>
      <p className="hint">
        {field.hint || 'Handlebars such as {{{imageUrl}}} pass through untouched.'}
      </p>

      {pickerOpen && (
        <ChooseImageModal toggleModal={() => setPickerOpen(false)} setImage={chooseFromLibrary} />
      )}
    </div>
  );
};

ImageSourceField.propTypes = {
  id: PropTypes.string.isRequired,
  // One entry of FIELDS.image, same descriptor shape <Field> takes.
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    hint: PropTypes.string,
  }).isRequired,
  value: PropTypes.string,
  // The block's current alt text, so the util can tell whether the asset's
  // description is allowed to fill it in.
  alt: PropTypes.string,
  // Takes a PATCH, not a (key, value) pair: choosing an asset can set both src
  // and alt, and they must land in the document as one change.
  onPatch: PropTypes.func.isRequired,
};

ImageSourceField.defaultProps = {
  value: '',
  alt: '',
};

export default ImageSourceField;
